import React, { useEffect, useState } from 'react';

// Components
import { Answers } from './Answers';
import { Button } from './Button';
import { Heading } from './Heading';

// i18n
import { useTranslation } from '../translations/useTranslation';

// Interfaces
import {
  Question,
  ComplementaryField,
  ComplementaryAnswer,
} from '../interfaces/Question';
import InfoIcon from './InfoIcon';
import Tooltip from './Tooltip';

interface Props {
  currentStep: number;
  question: Question;
  isMemberOfAkassa: boolean;
  onChange: (question: Question) => void;
  onComplementaryAnswersChange: (question: Question) => void;
  onNext: () => void;
  onPrevious: (previousId: number | undefined) => void;
}

function getLengthofComplementaryAnswers(
  fields: ComplementaryField[],
  answers: Record<string, string> | null
): boolean {
  if (answers) {
    const filledFields = fields.filter((field) => {
      return answers[field.name].length > 0;
    });
    return filledFields.length === fields.length;
  }

  return false;
}

export const Step: React.FC<Props> = ({
  currentStep,
  question,
  isMemberOfAkassa,
  onChange,
  onComplementaryAnswersChange,
  onNext,
  onPrevious,
}) => {
  const [givenAnswer, setGivenAnswer] = useState<string | null>(
    question.givenAnswer
  );
  const [complementaryAnswers, setComplementaryAnswers] =
    useState<ComplementaryAnswer | null>(
      question.complementaryAnswers ? question.complementaryAnswers : null
    );
  const { t, keys, locale } = useTranslation();

  useEffect(() => {
    setGivenAnswer(question.givenAnswer);
  }, [question]);

  if (currentStep !== question.id) {
    return null;
  }

  const heading = t(question.heading);
  const title = t(question.question);
  const hideTitle = question.hideQuestionTitle;

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    onChange({
      ...question,
      givenAnswer: value,
    });
    setGivenAnswer(value);
  };

  const handleOnPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    onPrevious(question.previousId);
  };

  const handleOnComplementaryChange = (answers: Record<string, string>) => {
    setComplementaryAnswers(answers);
    onComplementaryAnswersChange({
      ...question,
      complementaryAnswers: answers,
    });
  };

  // Check if the given answer is valid
  const answerValid = givenAnswer !== null && givenAnswer.length > 0;

  // Pick the question that matches the given answer
  const selectedQuestion =
    question.possibleAnswers &&
    question.possibleAnswers.find((anw) => {
      return anw.value === givenAnswer;
    });

  // Check if the selected question has complementary fields
  const hasComplementaryFields = selectedQuestion
    ? typeof selectedQuestion.complementaryFields !== 'undefined'
    : false;

  // If the question has complementary fields then check if all the fields have been filled by the user (defualt to false)
  const complementaryFieldsFilled = selectedQuestion?.complementaryFields
    ? getLengthofComplementaryAnswers(
        selectedQuestion.complementaryFields,
        complementaryAnswers
      )
    : false;

  // Check if all the requirements are met for enabling the next button
  // if there are complementary fields then check if all of them are filled and the answer is valid
  // else just check if the answer if valid
  const enableNextButton = hasComplementaryFields
    ? complementaryFieldsFilled && answerValid
    : answerValid;

  return (
    <div className="flex flex-column gap-xs">
      <div className="flex flex-column gap-2xs">
        <h1 className="text-2xl">{heading}</h1>
        {!hideTitle && (
          <div className="inline-flex gap-4xs items-center">
            <h2 className='font-primary letter-spacing-reset text-md'>{title}</h2>
            {(question.key === 'HigherEducationCredit' ||
              question.key === 'InternationalEducation') && (
              <Tooltip
                content={
                  <p className='text-sm'>
                    {question.key === 'HigherEducationCredit' &&
                      t(keys.questionHowManyEducationPointsTooltipContent)}
                    {question.key === 'InternationalEducation' &&
                      t(
                        keys.questionDoYouHaveAInternationalEducationTooltipContent
                      )}
                  </p>
                }
                trigger={
                  <span tabIndex={0} className="membership-app__step-higher-education-info">
                    <InfoIcon size="md" />
                  </span>
                }
              />
            )}
          </div>
        )}
        {question.description && <p className='text-base'>{t(question.description)}</p>}
      </div>
      <Answers
        type={question.type}
        name={question.question}
        answers={question.possibleAnswers}
        givenAnswer={givenAnswer}
        complementaryAnswers={question.complementaryAnswers}
        onChange={handleOnChange}
        onComplementaryChange={handleOnComplementaryChange}
      />
      <div className='flex gap-2xs'>
        <Button
          title={t(keys.uiButtonNext)}
          onClick={onNext}
          disabled={!enableNextButton}
        />
        {(question.previousId || isMemberOfAkassa) && (
          <div className="membership-app__step-back">
            <Button
              layout="secondary"
              onClick={handleOnPrevious}
              title={t(keys.uiButtonGoBack)}
            />
          </div>
        )}
      </div>
    </div>
  );
};
